import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { Translate } from 'react-localize-redux'
import { connect } from 'react-redux'
import { IAppState } from '../../core/store/store'
import { IUsersState, IUsersStateProps, TUsersProps } from './Users.types'
import {
  createStdUser,
  deleteUser,
  disableUserEdit,
  fetchGroups,
  fetchUsersList,
  impersonateUser,
  resendSsoEmail,
  resendVerificationLink,
  resetMfaLink
} from '../../core/actions/Users.actions'
import {
  Button,
  ButtonDropDown,
  ButtonDropDownMenuProps,
  FreeTextFilter,
  ModalTypes,
  SelectFilter,
  SideRail,
  StandardModal,
  Table,
  TableColumn,
  TableMetaProps
} from '@digicert/dcone-common-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faPencil, faTrashAlt, faUnlock } from '@fortawesome/pro-light-svg-icons'
import { LIMIT_10, RouterConfig } from '../../core/utils/constants'
import { CERT_CENTRAL_SHORT_APP_CODE, CreateUserTypes, DIGIROOT_ID, getDisplayAppCode } from 'core/constants'
import styles from './Users.module.scss'
import { IAccessScope, IUser } from '../../reducers/Reducers.interfaces'
import { translate } from '../../shared/helpers/utils'
import { Link, useLocation } from 'react-router-dom'
import { ISort } from '../../core/actions/Actions.interfaces'
import { statusRender } from '../../core/utils/renders.helpers'
import isEqual from 'fast-deep-equal'
import NoPermissionsPage from 'shared/components/no-permissions-page'
import { getItemFromLS } from 'shared/helpers/LocalStorage'
import { resizeTableBody } from '../../shared/helpers/Table'
import cn from 'classnames'
import CreateUserWithRole from './create-user-with-role/CreateUserWithRole'
import { Icon } from '@fortawesome/fontawesome-svg-core';
import EditUserSideRail from './user-details/EditUserSideRail'
import { UserUpdateType } from './user-details/user-overview/UserOverview.types'
import { initLocalization } from 'core/actions/Localization.actions'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import UserAccessLayout from './UserAccessLayout'
import { Source } from 'secure-pages/groups/Groups.types'

const Users = (
  {
    user,
    permissions,
    fetchUsersList,
    profile,
    profileId,
    deleteUser,
    createStdUser,
    resendSsoEmail,
    localeList,
    initLocalization,
    fetchGroups,
    resendVerificationLink,
    resetMfaLink,
    impersonateUser,
    enableImpersonation
  }: TUsersProps): ReactElement<IUsersState> => {
  const storedTableData = JSON.parse(getItemFromLS('dcone-table/user-table-' + profileId) as string)
  const accountIdFromLS = getItemFromLS('accountId')
  const defaultFilters = {
    status: [
      {
        value: 'LOCKED',
        func: "eq",
        label: translate('common.statuses.locked') as string,
        key: 'common.statuses.locked'
      },
      {
        value: 'PENDING',
        func: "eq",
        label: translate('common.statuses.pending') as string,
        key: 'common.statuses.pending'
      },
      {
        value: 'ACTIVE',
        func: "eq",
        label: translate('common.statuses.active') as string,
        key: 'common.statuses.active'
      }
    ],
    ...(accountIdFromLS && {
      accountId: [{
        value: accountIdFromLS,
        label: accountIdFromLS
      }]
    })
  }

  const defaultMeta: TableMetaProps<IUser> = {
    pageSize: LIMIT_10,
    currentPage: 1,
    totalItems: 0,
    filters: getItemFromLS('dcone-table/user-table-' + profileId) === null ? defaultFilters : storedTableData.meta.filters,
    sort: {
      columnKey: 'firstName',
      order: 'ascend'
    }
  }
  const [current, setCurrent] = useState(1)
  const [sortedInfo, setSortedInfo] = useState({ columnKey: 'firstName', order: 'ascend' })
  const [userData, setUserData] = useState([{}] as IUser[])
  const [meta, setMeta] = useState(defaultMeta)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [usersToDelete, setUsersToDelete] = useState([] as string[])
  const [unlockUserDetails, setUnlockUserDetails] = useState<IUser>()
  const [loading, setLoading] = useState(true)
  const [showSideRail, setShowSideRail] = useState(false)
  const [showEditSideRail, setShowEditSideRail] = useState(false)
  const [showUnlockUserModal, setShowUnlockUserModal] = useState(false)
  const [sideRailContent, setSideRailContent] = useState<UserUpdateType>(UserUpdateType.UserOverview)
  const [userCreationType, setUserCreationType] = useState<string | undefined>(undefined)
  const [groups, setGroups] = useState([])
  const [userToUpdate, setUserToUpdate] = useState<IUser>()
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const userType = params.get('createUserType')

  useEffect(() => {
      if (accountIdFromLS) {
        localStorage.removeItem('accountId')
      }
      if (userType !== null) {
        setShowSideRail(true)
        setUserCreationType(userType === IAccessScope.system ? CreateUserTypes.DIGICERT_USER : CreateUserTypes.ACCOUNT_USER)
      }
  }, [])

  const renderAccountName = (user) => {
    if (user.accounts.length > 1 || user.access_scope === IAccessScope.system) {
      return translate('common.multipleAccounts') as string
    }

    else if (user.accounts.length === 0) {
      return ''
    }

    else return (
      <Link to={RouterConfig.accounts.details(user.accounts[0].id)}>
        {user.accounts[0].name}
      </Link>
    )
  }

  const getAccounts = (): any => {
    const options = profile?.accounts.filter(account => account.active).map((account) => {
      return {
        label: account.display_name,
        value: account.id,
        func: 'eq'
      }
    })
    options?.sort((a, b) => (a.label > b.label ? 1 : -1))
    return options
  }

  const renderGroupName = (user) => {
    if (user.groups?.length === 0) {
      return ''
    }
    return user.groups.map(g => translate(`groups.${g.name}`)).join(', ');
  }

  const getApplicationArray = (appObject): any => {
    const applications: Array<any> = []
    if (appObject) {
      Object.keys(appObject).reduce(
        (val, key) => {
          applications.push(appObject[key])
          return appObject[key]
        },
        {}
      )
    }
    return applications
  }

  const renderAppNames = (user) => {
    let appString = ''
    const appArray = getApplicationArray(user.applications)
    appArray.map((app, index) => {
      if (index === appArray.length - 1) {
        appString += app.name
      } else {
        appString += app.name + ", "
      }
    })
    return (
      <>
        {appString}
      </>
    )
  }

  const applicationOptions = (): any => {
    let applications: Array<any> = []
    if (profile && profile?.applications) {
      applications = getApplicationArray(profile.applications).map(app => {
        return {
          label: app.name,
          value: app.id,
          func: 'eq',
          key: `manageUser.services.${getDisplayAppCode(app.short_app_code)}`
        }
      })
    }
    applications?.sort((a, b) => (a.label > b.label ? 1 : -1))
    return applications
  }

  const getColumns = (): TableColumn<IUser>[] => {
    const cols: TableColumn<IUser>[] = [
      {
        key: 'userName',
        displayRowActions: user?.status !== 'DELETED',
        title: translate('common.form.username') as string,
        render: user => (
          <Link to={RouterConfig.administrators.details(user.id)}>
            {user.login}
          </Link>
        ),
        sorter: true,
        renderFilter: function BasicStringFilter() {
          return (
            <FreeTextFilter<IUser>
              label={translate('common.form.login') as string}
              columnKey='userName'
            />
          );
        }
      },
      {
        key: 'full_name',
        title: translate('common.form.fullName') as string,
        render: user => (
          <>{user.first_name} {user.last_name}</>
        ),
        sorter: true,
        renderFilter: function BasicStringFilter() {
          return (
            <FreeTextFilter<IUser>
              label={translate('common.form.fullName') as string}
              columnKey='full_name'
            />
          );
        }
      },
      {
        key: 'email',
        title: translate('common.form.email') as string,
        render: user => (<>{user.email}</>),
        sorter: true,
        renderFilter: function BasicStringFilter() {
          return (
            <FreeTextFilter<IUser>
              label={translate('common.form.email') as string}
              columnKey='email'
            />
          );
        }
      },
      {
        key: 'status',
        title: translate('common.status') as string,
        render: user => (
          statusRender(user)
        ),
        sorter: true,
        renderFilter: function BasicStringFilter() {
          return (
            <SelectFilter<IUser, string>
              items={[
                {
                  value: 'LOCKED',
                  label: translate('common.statuses.locked') as string,
                  func: 'eq',
                  //@ts-ignore
                  key: 'common.statuses.locked'
                },
                {
                  value: 'PENDING',
                  label: translate('common.statuses.pending') as string,
                  func: 'eq',
                  //@ts-ignore
                  key: 'common.statuses.pending'
                },
                {
                  value: 'ACTIVE',
                  label: translate('common.statuses.active') as string,
                  func: 'eq',
                  //@ts-ignore
                  key: 'common.statuses.active'
                },
                {
                  value: 'DELETED',
                  label: translate('common.statuses.deleted') as string,
                  func: 'eq',
                  //@ts-ignore
                  key: 'common.statuses.deleted'
                },
                {
                  value: 'DISABLED',
                  label: translate('common.statuses.disabled') as string,
                  func: 'eq',
                  //@ts-ignore
                  key: 'common.statuses.disabled'
                }
              ]}
              columnKey='status'
              label={translate('common.status') as string}
              minSearchLength={1}
            />
          );
        }
      },
      {
        key: 'accountId',
        title: translate('common.form.account') as string,
        hidden: profile && profile.access_scope !== IAccessScope.system,
        sorter: false,
        render: user => renderAccountName(user),
        renderFilter: (profile && profile.access_scope === IAccessScope.system) ? function BasicStringFilter() {
          return (
            <FreeTextFilter<IUser>
              label={translate('common.form.accountNameOrId') as string}
              columnKey='accountId'
            />
          );
        } : () => {
          return (
            <SelectFilter<IUser, string>
              multi={false}
              items={getAccounts()}
              columnKey='accountId'
              label={translate('common.form.account') as string}
              minSearchLength={1}
            />)
        }
      },
      {
        key: 'groups',
        title: translate('common.userGroup') as string,
        render: user => (
          renderGroupName(user)
        ),
        sorter: false,
        renderFilter: function BasicStringFilter() {
          return (
            <SelectFilter<IUser, string>
              items={groups}
              columnKey='groups'
              label={translate('manageUser.administrators.groups') as string}
              minSearchLength={1}
            />
          );
        },
      },
      {
        key: 'applications',
        title: translate('manageUser.administrators.enabledServices') as string,
        sorter: false,
        render: user => renderAppNames(user),
        renderFilter: function BasicStringFilter() {
          return (
            <SelectFilter<IUser, string>
              items={applicationOptions()}
              columnKey='applications'
              label={translate('manageUser.administrators.enabledServices') as string}
              minSearchLength={1}
            />
          );
        },
      }
    ]

    return cols
  }

  const parseFilter = (newMeta) => {
    if (newMeta.filters) {
      const tmpArray: any = Object.getOwnPropertyNames(newMeta.filters).map((key: string) => {
        if (newMeta.filters) {
          const item = newMeta.filters[key]

          if (item) {
            // format access_scope key to be compatible with backend query which is in camel case
            if (key === 'has_permissions') {
              return {
                ['hasPermissions']: newMeta.filters[key].map((option) => {
                  return option.value
                })
              }
            }
            if (key === 'applications') {
              return {
                ['applicationIds']: newMeta.filters[key].map((option) => {
                  return option.value
                })
              }
            }
            if (key === 'groups') {
              return {
                ['groupIds']: newMeta.filters[key].map((option) => {
                  return option.value
                })
              }
            }
            if (
              key === "accountId" &&
              profile?.access_scope === IAccessScope.system
            ) {
              return {
                ["accountNameLike"]: newMeta.filters[key].map((option) => {
                  return option.value;
                }),
              };
            }
            if (key === 'full_name') {
              return {
                ['fullNameLike']: newMeta.filters[key].map((option) => {
                  return option.value
                })
              }
            }

            else {
              return {
                [key]: newMeta.filters[key].map((option) =>  {
                  return option.value
                })
              }
            }
          }
        }
      });

      return Object.assign({}, ...tmpArray)
    }

    // clear filters
    else {
      return Object.assign({}, {})
    }
  }

  const getUsers = useCallback((meta, onFirstMount?: boolean): Promise<any> => {
    const params: ISort = { limit: meta.pageSize, offset: meta.pageSize * (meta.currentPage - 1) }

    params.sort = [{
      columnKey: `${meta.sort.columnKey === 'full_name' ? 'first_name' : meta.sort.columnKey}:${meta.sort.order === 'descend' ? 'desc' : 'asc'}`
    }]

    return fetchUsersList(params, parseFilter(meta)).then((response: any) => {
      if (onFirstMount) {
        setMeta({ ...meta, ...{ totalItems: response.total } });
      }
      return response
    })
  },
    [fetchUsersList]
  )

  const tableChange = useCallback(
    (newMeta: TableMetaProps<IUser>) => {
      let newSortedInfo = {
        columnKey: newMeta.sort?.columnKey,
        order: newMeta.sort?.order
      }

      // set to ascending by name if undefined
      if (newSortedInfo.columnKey === undefined && newSortedInfo.order === undefined) {
        newSortedInfo = {
          columnKey: 'firstName',
          order: 'ascend'
        }
      }

      // on sort go back to page 1
      if (!isEqual(newSortedInfo, sortedInfo)) {
        setCurrent(newMeta.currentPage = 1)
        setSortedInfo({
          columnKey: newMeta.sort?.columnKey || '',
          order: newMeta.sort?.order || ''
        })
      }

      if (!isEqual(newMeta.currentPage, current)) {
        setCurrent(newMeta.currentPage)
        setMeta({ ...newMeta, pageSize: newMeta.pageSize * (newMeta.currentPage - 1) })
      }

      if (!isEqual(newMeta.filters, meta.filters)) {
        setCurrent(newMeta.currentPage = 1)
        setMeta({ ...newMeta, filters: parseFilter(newMeta) })
      }

      setMeta({ ...newMeta })

      getUsers(newMeta).then((response) => {
        setUserData(response.items)
        setLoading(false)
        setMeta({ ...newMeta, ...{ totalItems: response.total } });
      })
    },
    [current, getUsers, meta.filters, sortedInfo]
  )

  const onEditUserAction = (userId: string): void => {
    setShowEditSideRail(true)
    setSideRailContent(UserUpdateType.UserDetails)
    setUserToUpdate(userData.filter(user => user.id === userId)[0])
  }

  const closeDeleteModal = () => {
    setDeleteModalVisible(false)
  }

  const onDeleteUser = () => {
    if (usersToDelete) {
      deleteUser(usersToDelete).then(() => {
        setDeleteModalVisible(false)
        setUsersToDelete([])
        getUsers(meta).then((response) => {
          setUserData(response.items)
        })
      })
    }
  }

  const prepareUserToDelete = (userId) => {
    setUsersToDelete([userId])
    setDeleteModalVisible(true)
  }

  const updateAccountAccess = (userId: string) => {
    setShowEditSideRail(true)
    setSideRailContent(UserUpdateType.AccountAccess)
    setUserToUpdate(userData.filter(user => user.id === userId)[0])
  }

  const resetUserCredentials = (userId: string) => {
    setShowEditSideRail(true)
    setSideRailContent(UserUpdateType.UserCredentials)
    setUserToUpdate(userData.filter(user => user.id === userId)[0])
  }

  const sendUnlockUserEmail = (userId: string) => {
    const user = userData.filter(user => user.id === userId)[0]
    setShowUnlockUserModal(true)
    setUnlockUserDetails(user)
  }

  const unlockUser = () => {
    if(unlockUserDetails) {
      if (unlockUserDetails.can_unlock_with_mfa) {
        resetMfaLink(unlockUserDetails.id, unlockUserDetails.status === 'LOCKED', unlockUserDetails.user_name,  <Translate id={'notifications.users.unlockUser'} data={{ username: unlockUserDetails.user_name }} />).then(() => {
          setShowUnlockUserModal(false)
        })
      } else if (unlockUserDetails.can_unlock_with_password) {
        resendVerificationLink(unlockUserDetails.id, unlockUserDetails.status === 'LOCKED', <Translate id={'notifications.users.unlockUser'} data={{ username: unlockUserDetails.user_name }} />).then(() => {
          setShowUnlockUserModal(false)
        })
      }
    }
  }

  const updateServices = (userId: string) => {
    setShowEditSideRail(true)
    setSideRailContent(UserUpdateType.UserServices)
    setUserToUpdate(userData.filter(user => user.id === userId)[0])
  }

  const onCreateStdUser = (id: string) => {
    createStdUser(id).then()
  }

  const onResendSsoEmail = (id: string) => {
    resendSsoEmail(id).then()
  }

  const onResendVerificationLink = (id: string) => {
    if (userData) {
      const user = userData.find(user => user?.id === id)
      resendVerificationLink(id, user?.status === 'LOCKED').then()
    }
  }

  const prepareUserToImpersonate = (userId: string) => {
    impersonateUser(userId)
  }

  const getRowActions = (item) => {
    const canEditUser = item?.status === 'ACTIVE' && (item?.access_scope === IAccessScope.system ?
      (item?.id === DIGIROOT_ID ? profile?.can_edit_root_user : profile?.can_edit_system_user) : profile?.can_edit_account_user)

    const isScimUser = item?.source === Source.SCIM
    const isAccountAdmin = profile?.access_scope === IAccessScope.account
    const isSystemUser = profile?.access_scope === IAccessScope.system

    // For non-SCIM users: can delete if not DELETED and not self
    // For SCIM users: account admin cannot delete, system users can delete
    const canDeleteUser = item?.status !== 'DELETED' && item.id !== profile?.id &&
      (item?.access_scope === IAccessScope.system ?
        (item?.id === DIGIROOT_ID ? profile?.can_edit_root_user : profile?.can_edit_system_user) :
        profile?.can_edit_account_user) &&
      (isScimUser ? isSystemUser : true)

    const canResendEmail = item?.access_scope === IAccessScope.system ? profile?.can_edit_system_user : profile?.can_edit_account_user

    if (item?.status !== 'DELETED' && item?.status !== 'DISABLED') {
      return [
        {
          title: translate('manageUser.administrators.table.editAction') as string,
          isVisible: canEditUser && !disableUserEdit(item),
          onClick: () => onEditUserAction(item.id),
          icon: <FontAwesomeIcon icon={faPencil as Icon as IconProp}/>
        },
        {
          title: translate('manageUser.administrators.table.resendAction') as string,
          isVisible: item.can_send_reset_password_email && canResendEmail,
          onClick: () => onResendVerificationLink(item.id),
          icon: <FontAwesomeIcon icon={faEnvelope as Icon as IconProp}/>
        },
        {
          title: translate('manageUser.administrators.table.inviteStdUser') as string,
          isVisible: item.invite_std_user,
          onClick: () => onCreateStdUser(item.id),
          icon: <FontAwesomeIcon icon={faEnvelope as Icon as IconProp}/>
        },
        {
          title: translate('manageUser.administrators.table.resendSsoEmail') as string,
          isVisible: item.can_resend_sso_email,
          onClick: () => onResendSsoEmail(item.id),
          icon: <FontAwesomeIcon icon={faEnvelope as Icon as IconProp}/>
        },
        {
          title: translate('notifications.users.details.updateAccountAccess') as string,
          isVisible: canEditUser && !disableUserEdit(item) && item?.id !== profile?.id,
          onClick: () => updateAccountAccess(item.id),
          icon: <FontAwesomeIcon icon={faPencil as Icon as IconProp}/>
        },
        {
          title: translate('notifications.users.details.updateUserServices') as string,
          isVisible: canEditUser && !disableUserEdit(item) && item.access_scope !== IAccessScope.system && item?.id !== profile?.id,
          onClick: () => updateServices(item.id),
          icon: <FontAwesomeIcon icon={faPencil as Icon as IconProp}/>
        },
        {
          title: translate('notifications.users.details.resetUserCredentials') as string,
          isVisible: item?.status !== 'LOCKED' && item.status !== 'PENDING',
          onClick: () => resetUserCredentials(item.id),
          icon: <FontAwesomeIcon icon={faEnvelope as Icon as IconProp}/>
        },
        {
          title: translate('manageUser.administrators.table.unlockUser') as string,
          isVisible: canResendEmail && item?.status === 'LOCKED' && (item.can_unlock_with_mfa || item.can_unlock_with_password),
          onClick: () => sendUnlockUserEmail(item.id),
          icon: <FontAwesomeIcon icon={faUnlock as Icon as IconProp}/>
        },
        {
          title: translate('manageUser.administrators.table.deleteAction') as string,
          isVisible: canDeleteUser,
          onClick: () => prepareUserToDelete(item.id),
          icon: <FontAwesomeIcon icon={faTrashAlt as Icon as IconProp}/>
        },
        {
          title: translate('manageUser.administrators.table.impersonateUser') as string,
          isVisible: enableImpersonation && isSystemUser && item?.access_scope === IAccessScope.account && item?.status === 'ACTIVE' && Object.values(item?.applications || {}).some((app: any) => app.short_app_code === CERT_CENTRAL_SHORT_APP_CODE),
          onClick: () => prepareUserToImpersonate(item.id),
        },
      ];
    } else {
      return [{}]
    }

  };

  const showCreateUser = (userType: string) => {
    setShowSideRail(true)
    if (userType) {
      setUserCreationType(userType)
    }
  }

  const onCreate = () => {
    getUsers(meta).then((response) => {
      setUserData(response.items)
      setShowSideRail(false)
    })
  }

  const renderUserActions = () => {
    const menuItems: ButtonDropDownMenuProps['items'] = [
      {
        key: 'creat-account-user',
        label: translate('manageUser.administrators.account') as string,
        onClick: () => showCreateUser(CreateUserTypes.ACCOUNT_USER)
      }
    ]

    if (profile?.access_scope === IAccessScope.system && permissions?.canManageSystemUser) {
      menuItems.push({
        key: 'creat-system-user',
        label: translate('manageUser.administrators.system') as string,
        onClick: () => showCreateUser(CreateUserTypes.DIGICERT_USER)
      })
    }

    if (profile?.access_scope === IAccessScope.system && permissions?.canManageSystemUser) {
      return (
        <ButtonDropDown
          label={translate('common.form.buttons.createUser') as string}
          placement="bottomLeft"
          size="small"
          trigger={['click']}
          type="primary"
          items={menuItems}
        />
      )
    }

    else {
      return (
        <Button size="small" onClick={() => showCreateUser(CreateUserTypes.ACCOUNT_USER)}>
          {translate('common.form.buttons.createUser') as string}
        </Button>
      )
    }
  }

  useEffect(() => {
    fetchGroups().then(groupList => {
      const groupOptions = groupList?.map(group => {
        return {
          value: group.id,
          label: translate(`groups.${group.name}`),
          description: translate(`groups.descriptions.${group.description}`),
          key: `groups.${group.name}`
        }
      })
      setGroups(groupOptions)
    })
  }, [])

  useEffect(() => {
    if (!loading) {
      const tableElement = document.getElementsByClassName('resizable-table-body')[0] as HTMLElement;
      if (tableElement) {
        resizeTableBody(tableElement)
        window.addEventListener('resize', () => resizeTableBody(tableElement), false)
      }
      return (): void => {
        window.removeEventListener('resize', () => resizeTableBody(tableElement), false)
      }
    }
  }, [loading])

  if (permissions) {
    const permissionArray = [permissions.canViewUser, permissions.canManageAccountUser, permissions.canManageSystemUser]
    if (!permissionArray.includes(true)) {
      return (
        <NoPermissionsPage />
      )
    }
  }

  const updateUsers = () => {
    getUsers(meta).then((response) => {
      setUserData(response.items)
    })
  }

  return (
    <section className={styles.users}>
      <UserAccessLayout />
      <div className={styles.content}>
        <header className={styles.header}>
          <h1><Translate id='Users' /></h1>
          <div>{renderUserActions()}</div>
        </header>

        <Table
          className={styles.table + ' ' + 'resizable-table-body'}
          columns={getColumns()}
          items={userData}
          getRowKey={user => user.id}
          //@ts-ignore
          getActions={(item) => getRowActions(item)}
          actionsButtonsQuantity={1}
          meta={meta}
          disableCheckBoxes
          onChange={tableChange}
          loading={loading}
          saveDataName={'user-table-' + profileId}
        />
      </div>

      {deleteModalVisible &&
        <StandardModal
          modalType={ModalTypes.DESTRUCTIVE}
          headline={translate('manageUser.administrators.delete.title') as string}
          display={deleteModalVisible}
          buttonLabel={translate('manageUser.administrators.delete.deleteUser') as string}
          buttonCancelLabel={translate('common.form.buttons.cancelBtn') as string}
          onOkCallBack={onDeleteUser}
          onCancelCallBack={closeDeleteModal}
        >
          <Translate id='manageUser.administrators.delete.content' />
        </StandardModal>
      }
      {showUnlockUserModal && (
        <StandardModal
          modalType={ModalTypes.STANDARD}
          headline={translate('unlockUser.header') as string}
          buttonLabel={translate('unlockUser.unlock') as string}
          buttonCancelLabel={translate('common.form.buttons.cancelBtn') as string}
          display={showUnlockUserModal}
          onOkCallBack={unlockUser}
          onCancelCallBack={() => {
            setShowUnlockUserModal(false)
          }}
        >
          <Translate id='unlockUser.content' />
                <br/><br/>
          <Translate id='unlockUser.content1' />
        </StandardModal>
      )}

      <SideRail
        className={cn(
          styles.sideRail,
          showSideRail && styles.expanded,
          userCreationType === CreateUserTypes.DIGICERT_USER && styles.system,
          userCreationType !== CreateUserTypes.DIGICERT_USER && styles.account,
        )}
        headline={
          <h2>
            {userCreationType === CreateUserTypes.DIGICERT_USER
              ? (<Translate id='manageUser.administrators.createSystemUser' />)
              : (<Translate id='manageUser.administrators.createAccountUser' />)
            }
          </h2>
        }
        show={showSideRail}
        onDismiss={() => setShowSideRail(!showSideRail)}
        lightbox
      >
        <div className={styles.createUserContent}>
          {showSideRail && (
            <CreateUserWithRole
              permissions={permissions}
              localeList={localeList}
              profileAccessScope={profile?.access_scope}
              userCreationType={userCreationType}
              onCancel={() => setShowSideRail(false)}
              onCreate={() => onCreate()}
            />
          )}
        </div>
      </SideRail>
      <EditUserSideRail
        user={userToUpdate}
        showSideRail={showEditSideRail}
        sideRailContent={sideRailContent}
        setShowSideRail={setShowEditSideRail}
        updateDetails={updateUsers}
        profile={profile}
        initLocalization={initLocalization}
      />

    </section>
  )
}

export default connect(
  (state: IAppState): IUsersStateProps => {
    return {
      user: state.profile.data,
      users: state.users.items,
      profile: state.profile.data,
      profileId: state.profile.data?.id,
      localeList: state.locales,
      enableImpersonation: state.auth.enableImpersonation
    }
  },
  {
    fetchUsersList,
    deleteUser,
    initLocalization,
    resendVerificationLink,
    resetMfaLink,
    createStdUser,
    resendSsoEmail,
    fetchGroups,
    impersonateUser
  }
)(Users)
